/**
 * Cliente WebSocket nativo (reemplaza a socket.io-client).
 */
import type { ClientMsg, ServerMsg, ServerMsgType } from '@share-paint/shared';

type Handler<T extends ServerMsgType> = (
  payload: Extract<ServerMsg, { type: T }>['payload'],
) => void;

const handlers: Map<string, Set<(payload: unknown) => void>> = new Map();

let socket: WebSocket | null = null;
let currentCode: string | null = null;
let urlBase = '';
let lastUrl = '';

function log(...args: unknown[]): void {
  // eslint-disable-next-line no-console
  console.log('[ws]', ...args);
}

function baseUrl(): string {
  if (urlBase) return urlBase;
  const env = (import.meta.env.VITE_WS_URL ?? '').trim();
  if (env) {
    urlBase = env.replace(/\/$/, '');
    return urlBase;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    urlBase = `${proto}://${window.location.host}`;
    return urlBase;
  }
  urlBase = 'ws://localhost:8787';
  return urlBase;
}

export function connect(code: string): WebSocket {
  const url = `${baseUrl()}/room/${code}`;
  lastUrl = url;

  if (socket && currentCode === code && socket.readyState !== WebSocket.CLOSED && socket.readyState !== WebSocket.CLOSING) {
    log('reuse socket for code=' + code, 'readyState=' + socket.readyState);
    return socket;
  }
  if (socket) {
    log('closing previous socket (code=' + currentCode + ', state=' + socket.readyState + ')');
    try { socket.close(); } catch { /* noop */ }
    socket = null;
  }

  currentCode = code;
  log('connecting →', url);
  const ws = new WebSocket(url);
  socket = ws;

  ws.addEventListener('open', () => {
    log('OPEN');
    const set = handlers.get('__open');
    if (set) for (const fn of set) fn(undefined);
  });

  ws.addEventListener('close', (ev) => {
    log('CLOSE code=' + ev.code + ' reason=' + (ev.reason || '-') + ' clean=' + ev.wasClean);
    const set = handlers.get('__close');
    if (set) for (const fn of set) (fn as (a: number, b: string) => void)(ev.code, ev.reason || '');
  });

  ws.addEventListener('error', () => {
    log('ERROR (see browser devtools for details)');
    const set = handlers.get('__error');
    if (set) for (const fn of set) (fn as () => void)();
  });

  ws.addEventListener('message', (ev) => {
    let msg: ServerMsg;
    try {
      msg = JSON.parse(String(ev.data)) as ServerMsg;
    } catch (err) {
      log('message parse error', err);
      return;
    }
    if (msg.type !== 'tick') {
      log('←', msg.type, stringifyPayload(msg));
    }
    const set = handlers.get(msg.type);
    if (set) for (const fn of set) fn((msg as { payload?: unknown }).payload);
  });

  return ws;
}

export function getSocket(): WebSocket | null {
  return socket;
}

function stringifyPayload(msg: any): string {
  const p = msg?.payload;
  if (p === undefined) return '(no payload)';
  try {
    const s = JSON.stringify(p);
    return (s ?? '').slice(0, 160);
  } catch {
    return '(unserialisable)';
  }
}

export function send(msg: ClientMsg): void {
  const payload = JSON.stringify(msg);
  if (msg.type !== 'submit_strokes' && msg.type !== 'request_snapshot') {
    log('→', msg.type, stringifyPayload(msg));
  }
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(payload);
    return;
  }
  log('send before open, queueing. socket state=' + (socket?.readyState ?? 'null'));
  const ws = socket ?? connect(currentCode ?? '');
  const once = () => {
    log('queue-drained: sending', msg.type);
    ws.removeEventListener('open', once);
    ws.send(payload);
  };
  ws.addEventListener('open', once);
}

export function on<T extends ServerMsgType>(type: T, fn: Handler<T>): () => void {
  let set = handlers.get(type);
  if (!set) {
    set = new Set();
    handlers.set(type, set);
  }
  set.add(fn as (payload: unknown) => void);
  return () => set!.delete(fn as (payload: unknown) => void);
}

export function onOpen(fn: () => void): () => void {
  let set = handlers.get('__open');
  if (!set) {
    set = new Set();
    handlers.set('__open', set);
  }
  set.add(fn);
  return () => set!.delete(fn);
}

export function onClose(fn: (code: number, reason: string) => void): () => void {
  let set = handlers.get('__close');
  if (!set) {
    set = new Set();
    handlers.set('__close', set);
  }
  set.add(fn as (p: unknown) => void);
  return () => set!.delete(fn as (p: unknown) => void);
}

export function onError(fn: () => void): () => void {
  let set = handlers.get('__error');
  if (!set) {
    set = new Set();
    handlers.set('__error', set);
  }
  set.add(fn);
  return () => set!.delete(fn);
}

export function debugInfo(): { url: string; state: number | null; code: string | null } {
  return {
    url: lastUrl,
    state: socket?.readyState ?? null,
    code: currentCode,
  };
}
